import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';

interface SubAgentCreationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config: {
    name: string;
    display_name: string;
    description: string;
  }) => void;
  existingNames: string[];  // To check for duplicates
  processorName: string;  // The processor being dropped
}

/**
 * Dialog for creating a new sub-agent (SWRM-121 Phase 3)
 * 
 * Prompts user for:
 * - name (snake_case, validated)
 * - display_name (human-readable)
 * - description (for LLM tool generation)
 */
export const SubAgentCreationDialog: React.FC<SubAgentCreationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  existingNames,
  processorName,
}) => {
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDisplayName('');
      setDescription('');
      setNameError('');
      setNameManuallyEdited(false);
    }
  }, [isOpen]);

  // Auto-generate name from display name if user hasn't manually edited it
  const handleDisplayNameChange = (value: string) => {
    setDisplayName(value);
    
    // Only auto-generate if user hasn't manually edited the name field
    if (!nameManuallyEdited) {
      // Convert display name to snake_case
      const autoName = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')  // Replace non-alphanumeric with underscore
        .replace(/^_+|_+$/g, '')       // Remove leading/trailing underscores
        .replace(/_+/g, '_');          // Collapse multiple underscores
      
      setName(autoName);
    }
  };

  // Track manual edits to name field
  const handleNameChange = (value: string) => {
    setName(value);
    setNameManuallyEdited(true);
  };

  // Validate name as user types
  useEffect(() => {
    if (!name) {
      setNameError('');
      return;
    }

    // Check snake_case format
    const snakeCasePattern = /^[a-z][a-z0-9_]*$/;
    if (!snakeCasePattern.test(name)) {
      setNameError('Name must be snake_case (lowercase letters, numbers, underscores only)');
      return;
    }

    // Check for duplicates
    if (existingNames.includes(name)) {
      setNameError('A sub-agent with this name already exists');
      return;
    }

    setNameError('');
  }, [name, existingNames]);

  const handleConfirm = () => {
    // Final validation
    if (!displayName.trim()) {
      // Display name is now required
      return;
    }

    if (!name.trim()) {
      setNameError('Name is required');
      return;
    }

    onConfirm({
      name: name.trim(),
      display_name: displayName.trim(),
      description: description.trim() || `Sub-agent for ${processorName}`,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !nameError && name.trim() && displayName.trim()) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Create Sub-Agent
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Adding <span className="font-medium">{processorName}</span> as the first processor in a new sub-agent chain.
          </div>

          {/* Display name input (now first) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Display Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => handleDisplayNameChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="
                w-full px-3 py-2 text-sm
                border border-gray-300 dark:border-gray-600 rounded-md
                bg-white dark:bg-gray-700
                text-gray-900 dark:text-gray-100
                focus:outline-none focus:ring-2 focus:ring-blue-500
              "
              placeholder="e.g., Coding Agent"
              autoFocus
            />
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Human-readable name (auto-generates Name field below)
            </div>
          </div>

          {/* Name input (now second) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`
                w-full px-3 py-2 text-sm
                border rounded-md
                ${nameError 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                }
                bg-white dark:bg-gray-700
                text-gray-900 dark:text-gray-100
                focus:outline-none focus:ring-2
              `}
              placeholder="e.g., coding_agent"
            />
            {nameError && (
              <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                <AlertCircle className="w-3 h-3" />
                {nameError}
              </div>
            )}
            {!nameError && name && (
              <div className="mt-1 text-xs text-green-600 dark:text-green-400">
                ✓ Valid snake_case name
              </div>
            )}
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Used as tool name (must be snake_case, auto-generated from Display Name)
            </div>
          </div>

          {/* Description input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="
                w-full px-3 py-2 text-sm
                border border-gray-300 dark:border-gray-600 rounded-md
                bg-white dark:bg-gray-700
                text-gray-900 dark:text-gray-100
                focus:outline-none focus:ring-2 focus:ring-blue-500
                resize-none
              "
              rows={3}
              placeholder="What does this sub-agent do?"
            />
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Shown to LLM when generating tool calls (optional but recommended)
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!displayName.trim() || !name.trim() || !!nameError}
            className={`
              px-4 py-2 text-sm font-medium text-white rounded-md transition-colors
              ${!displayName.trim() || !name.trim() || nameError
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
              }
            `}
          >
            Create Sub-Agent
          </button>
        </div>
      </div>
    </div>
  );
};

